import { authDelete, authGet, authPost, authPut } from "@api";

import {
  RegistryUpdatesResponse,
  Rule,
  RuleDefinition,
  Ruleset,
  RulesetRulePath,
  RulesetWithIds,
  ShallowRuleset,
  TestCaseHighlight,
  Visibility,
} from "@pages/registry/types";

export function fetchRulesetIndex(type: "unlisted"): Promise<Ruleset[]>;
export function fetchRulesetIndex(type?: "public"): Promise<ShallowRuleset[]>;
export function fetchRulesetIndex(
  type?: Visibility
): Promise<(ShallowRuleset | Ruleset)[]> {
  if (type === "unlisted") {
    return authGet<Ruleset[]>(
      `/api/registry/rulesets?type=unlisted&rule_ids=1&definition=1`
    );
  }
  return authGet<ShallowRuleset[]>(`/api/registry/rulesets`);
}

export function fetchMultitypeRulesets(
  types: Visibility[]
): Promise<Ruleset[]> {
  return authGet<Ruleset[]>(
    `/api/registry/rulesets?types=${types.join(",")}&rule_ids=1&definition=1`
  );
}

export function fetchRulesetRulePaths(): Promise<RulesetRulePath[]> {
  return authGet<RulesetRulePath[]>(`/api/registry/ruleset_rule_paths`);
}

export const fetchRuleset = (id: string) =>
  authGet<RulesetWithIds>(`/api/registry/rulesets/${id}`);
export const fetchRulesetWithDefinitions = (id: string) =>
  authGet<Ruleset>(`/api/registry/rulesets/${id}?definition=1&test_cases=1`);

/**
 * if you're fetching the whole registry (startsWith is undefined),
 * use the useRegistryRules hook instead
 */
export const fetchRules = (
  startsWith?: string
): Promise<(Rule & RuleDefinition)[]> => {
  const params: Record<string, string> = {
    definition: "1",
    test_cases: "1",
    include_dangerous: "0",
  };

  if (startsWith !== undefined) {
    params["starts_with"] = startsWith;
  }

  return authGet<(Rule & RuleDefinition)[]>(
    `/api/registry/rules?${new URLSearchParams(params).toString()}`
  );
};

export const fetchRule = (id: string): Promise<Rule & RuleDefinition> => {
  const data: Record<string, string> = { definition: "1", test_cases: "1" };

  return authGet<Rule & RuleDefinition>(
    `/api/registry/rules/${id}?${new URLSearchParams(data).toString()}`
  );
};

export type RulePostParams = {
  definition: object;
  language: string;
  test_target: string;
  visibility: Visibility;
  highlights?: TestCaseHighlight[];
  // only needed if visibility is org_private
  deployment_id?: string;
};

export type RulesetPostParams = {
  rules: { id: string }[];
  deployment_id?: string;
};

export const postRule = (data: RulePostParams) => {
  return authPost<RulePostParams, Rule>(`/api/registry/rules`, data);
};

export const postRuleset = (name: string, data: RulesetPostParams) => {
  return authPost<RulesetPostParams, RulesetWithIds>(
    `/api/registry/rulesets/${name}`,
    data
  );
};

type PublishParams = {
  checkId: string;
};

type PublishResponse = {
  url: string;
};

type UpdateParams = {
  visibility: Visibility;
  deployment_id: string;
};

type UpdateResponse = {
  status: string;
};

export const publishRule = (ruleId: string, checkId: string) =>
  authPost<PublishParams, PublishResponse>(
    `/api/registry/rules/${ruleId}/publish`,
    { checkId }
  );

export const publishRuleset = (rulesetName: string, checkId: string) =>
  authPost<PublishParams, PublishResponse>(
    `/api/registry/rulesets/${rulesetName}/publish`,
    { checkId }
  );

export const updateVisibility = (
  rulesetName: string,
  newVisibility: Visibility,
  deploymentId: string
) =>
  authPut<UpdateParams, UpdateResponse>(
    `/api/registry/rulesets/${rulesetName}/visibility`,
    { visibility: newVisibility, deployment_id: deploymentId }
  );

export const deleteUserRule = (rulesetName: string) =>
  authDelete<Ruleset>(`/api/registry/rulesets/${rulesetName}`);

export const restoreDeletedUserRule = (rulesetName: string) =>
  authPost<{ ruleset_name: string }, Ruleset>(
    `/api/registry/rulesets/deleted`,
    {
      ruleset_name: rulesetName,
    }
  );

export const fetchRegistryUpdates = (
  deployment_id?: string
): Promise<RegistryUpdatesResponse> => {
  return authGet<RegistryUpdatesResponse>(
    `/api/registry/orgs/${deployment_id}/rules/updated`
  );
};
